import { createContext, useContext } from 'react';

/** Payload used to configure a confirmation prompt instance. */
export interface ConfirmOptions {
    /** Heading shown at the top of the modal. */
    title: string;
    /** Optional explanatory copy shown under the heading. */
    description?: string;
    /** Label for the affirmative action button. */
    confirmLabel?: string;
    /** Label for the dismiss action button. */
    cancelLabel?: string;
}

/** One selectable action in a multi-choice prompt. */
export interface ChoiceOption {
    /** Value the `choose` promise resolves with when this action is picked. */
    id: string;
    label: string;
}

/** Payload used to configure a multi-choice prompt instance. */
export interface ChoiceOptions {
    title: string;
    description?: string;
    /** Label for the dismiss button, which resolves the prompt with `null`. */
    cancelLabel?: string;
    /** Actions shown after the dismiss button, in order; the last one is emphasized. */
    choices: readonly ChoiceOption[];
}

/** API exposed by the confirmation context. */
export interface ConfirmContextValue {
    confirm: (options: ConfirmOptions) => Promise<boolean>;
    /** Resolves with the picked choice's id, or `null` if the user cancels/dismisses. */
    choose: (options: ChoiceOptions) => Promise<string | null>;
}

export const ConfirmContext = createContext<ConfirmContextValue | undefined>(undefined);

/** The confirmation context where there is one, else undefined: for hooks that only offer what needs confirming when it can be asked. */
export function useOptionalConfirmContext(): ConfirmContextValue | undefined {
    return useContext(ConfirmContext);
}

/** Hook that reads the confirmation context. */
export function useConfirmContext(): ConfirmContextValue {
    const context = useContext(ConfirmContext);
    if (context === undefined) {
        throw new Error('useConfirmContext must be used within a ConfirmModalProvider');
    }

    return context;
}
